import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  AppBar,
  Toolbar, InputAdornment, InputLabel,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  TableContainer,
  MenuItem,
  Button,
  Select,
  FormControl,
  Chip,
  Snackbar,
  IconButton,
  useMediaQuery,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Modal,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MuiAlert from '@mui/material/Alert';
import LoadingBar from 'react-top-loading-bar';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import TableChartIcon from '@mui/icons-material/TableChart';
import SaveIcon from '@mui/icons-material/Save';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const TopicProgress = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // State for dropdowns
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');

  // State for dropdown data
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);

  // State for topics and UI
  const [topics, setTopics] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [remarkInputs, setRemarkInputs] = useState({});
  const [topicStatuses, setTopicStatuses] = useState({});
  const [searchText, setSearchText] = useState('');
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [isMaster, setIsMaster] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // State for Info Modal
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  // Fetch Branches Data
  const fetchBranches = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setBranches(data);
      } else {
        console.error("Failed to fetch branches");
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  useEffect(() => {
    const isMasterUser = sessionStorage.getItem("is_Master") === "true";
    setIsMaster(isMasterUser);

    if (!isMasterUser) {
      const branchId = sessionStorage.getItem("branchId");
      setSelectedBranchId(branchId);
      return;
    }

    // For master users
    fetchBranches().then(() => {
      // After branches are loaded → auto-select first one if nothing is selected
      setBranches((prevBranches) => {
        if (prevBranches.length > 0 && !selectedBranchId) {
          const defaultBranch = prevBranches[0];
          setSelectedBranchId(defaultBranch.branchId);
          // Optional: you can show a small info once
          // setSnackbarMessage(`Auto-selected branch: ${defaultBranch.branchName}`);
          // setOpenSnackbar(true);
          // setSnackbarSeverity('info');
        }
        return prevBranches;
      });
    });
  }, []);

  // Auto-select first branch when branches are loaded (for master users only)
  useEffect(() => {
    if (!isMaster || branches.length === 0 || selectedBranchId) {
      return;
    }

    // Select the first branch by default
    const defaultBranch = branches[0];
    setSelectedBranchId(defaultBranch.branchId);

    // Optional: feedback
    // setSnackbarMessage(`Default branch selected: ${defaultBranch.branchName}`);
    // setOpenSnackbar(true);
    // setSnackbarSeverity('info');

  }, [branches, isMaster, selectedBranchId]);

  // Fetch Teacher Data
  const fetchTeacherData = async () => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem('branchId');

      if (!branchId) {
        setTeachers([]);
        setLoadingBarProgress(0);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/AssignmentCreate/GetTeacher?Branch_Id=${branchId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({}),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setTeachers(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch teacher data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setTeachers([]);
      setLoadingBarProgress(0);
    }
  };

  // Fetch Class Data
  const fetchClassData = async (teacherId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem('branchId');
      if (!branchId) {
        setClasses([]);
        setSnackbarMessage('Branch ID is missing. Please select a branch first.');
        setOpenSnackbar(true);
        setSnackbarSeverity('warning');
        setLoadingBarProgress(0);
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/AssignmentCreate/GetClass?Branch_Id=${branchId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ teacherId }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setClasses(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch class data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setClasses([]);
      setLoadingBarProgress(0);
    }
  };

  // Fetch Section Data
  const fetchSectionData = async (classId, teacherId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/AssignmentCreate/ddlSection_clsId`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ classId, teacherId }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setSections(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch section data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setSections([]);
      setLoadingBarProgress(0);
    }
  };

  // Fetch Subject Data
  const fetchSubjectData = async (classId, teacherId, sectionId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem('branchId');
      if (!branchId) {
        setSubjects([]);
        setSnackbarMessage('Branch ID is missing. Please select a branch first.');
        setOpenSnackbar(true);
        setSnackbarSeverity('warning');
        setLoadingBarProgress(0);
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/AssignmentCreate/Subjectddl?Branch_Id=${branchId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ classId, teacherId, sectionId }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        setSubjects(Array.isArray(responseData) ? responseData : []);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch subject data');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setSubjects([]);
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setLoadingBarProgress(0);
    }
  };

  // Helper function to extract remark from topic
  const extractRemarkFromTopic = (topic) => {
    if (topic.coveredDescription) return topic.coveredDescription;
    if (topic.remarks) return topic.remarks;
    if (topic.remark) return topic.remark;
    if (topic.description) return topic.description;
    if (topic.note) return topic.note;
    if (topic.notes) return topic.notes;
    if (topic.comment) return topic.comment;
    if (topic.comments) return topic.comments;

    const stringFields = Object.keys(topic).filter(key => {
      const isString = typeof topic[key] === 'string';
      const keyLower = key.toLowerCase();
      const containsRemarkWord = (
        keyLower.includes('remark') ||
        keyLower.includes('desc') ||
        keyLower.includes('note') ||
        keyLower.includes('comment')
      );
      return isString && containsRemarkWord;
    });

    if (stringFields.length > 0) {
      return topic[stringFields[0]];
    }

    return '';
  };

  // Fetch Topics Data
  const fetchTopicsData = useCallback(async (classId, sectionId, subjectId) => {
    try {
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem('branchId');
      if (!branchId) {
        setTopics([]);
        setFilteredTopics([]);
        setSnackbarMessage('Branch ID is missing. Please select a branch first.');
        setOpenSnackbar(true);
        setSnackbarSeverity('warning');
        setLoadingBarProgress(0);
        return;
      }
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/TeachingTopics/GetByClassSubject?Branch_Id=${branchId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({ classId, sectionId, subjectId }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        const newTopics = responseData.topics || [];

        setTopics(newTopics);
        setFilteredTopics(newTopics);

        const statuses = {};
        const initialRemarks = {};

        newTopics.forEach(topic => {
          statuses[topic.topicId] = topic.status || 'P';
          initialRemarks[topic.topicId] = extractRemarkFromTopic(topic);
        });

        setTopicStatuses(statuses);
        setRemarkInputs(initialRemarks);
        setLoadingBarProgress(100);
      } else {
        setSnackbarMessage('Failed to fetch topics');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setLoadingBarProgress(0);
    }
  }, [isMaster, selectedBranchId]);

  // Filter topics based on search text - ONLY BY TOPIC NAME
  const handleSearch = () => {
    if (!searchText.trim()) {
      setFilteredTopics(topics);
      return;
    }

    const searchTerm = searchText.toLowerCase().trim();
    const filtered = topics.filter(topic =>
      topic.topicName?.toLowerCase().includes(searchTerm)
    );

    setFilteredTopics(filtered);
  };

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (!value.trim()) {
      setFilteredTopics(topics);
    } else {
      // Immediate search on typing
      const searchTerm = value.toLowerCase().trim();
      const filtered = topics.filter(topic =>
        topic.topicName?.toLowerCase().includes(searchTerm)
      );
      setFilteredTopics(filtered);
    }
  };

  // Handle search on Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Clear search when new subject is selected
  useEffect(() => {
    if (selectedSubject && selectedSection && selectedClass && selectedTeacher) {
      setSearchText('');
      setFilteredTopics(topics);
    }
  }, [selectedSubject, selectedSection, selectedClass, selectedTeacher, topics]);

  // Fetch Teacher Data on Component Mount
  useEffect(() => {
    if (selectedBranchId || !isMaster) {
      fetchTeacherData();

      // Reset all dependent selections when branch changes
      setSelectedTeacher('');
      setSelectedClass('');
      setSelectedSection('');
      setSelectedSubject('');
      setSearchText('');
      setTopics([]);
      setFilteredTopics([]);
    }
  }, [selectedBranchId, isMaster]);

  // Fetch Class Data when Teacher is Selected
  useEffect(() => {
    if (selectedTeacher) {
      fetchClassData(selectedTeacher.employeeId);
      setSelectedClass('');
      setSelectedSection('');
      setSelectedSubject('');
      setSearchText('');
      setFilteredTopics([]);
    }
  }, [selectedTeacher]);

  // Fetch Section Data when Class is Selected
  useEffect(() => {
    if (selectedClass && selectedTeacher) {
      fetchSectionData(selectedClass.classId, selectedTeacher.employeeId);
      setSelectedSection('');
      setSelectedSubject('');
      setSearchText('');
      setFilteredTopics([]);
    }
  }, [selectedClass, selectedTeacher]);

  // Fetch Subject Data when Section is Selected
  useEffect(() => {
    if (selectedSection && selectedClass && selectedTeacher) {
      fetchSubjectData(
        selectedClass.classId,
        selectedTeacher.employeeId,
        selectedSection.sectionId
      );
      setSelectedSubject('');
      setSearchText('');
      setFilteredTopics([]);
    }
  }, [selectedSection, selectedClass, selectedTeacher]);

  // Fetch Topics when Subject is Selected
  useEffect(() => {
    if (selectedSubject && selectedSection && selectedClass && selectedTeacher) {
      fetchTopicsData(
        selectedClass.classId,
        selectedSection.sectionId,
        selectedSubject.subjectId
      );
      setSearchText('');
    }
  }, [selectedSubject, selectedSection, selectedClass, selectedTeacher]);

  // Handle remark input change
  const handleRemarkChange = (topicId, value) => {
    setRemarkInputs(prev => ({
      ...prev,
      [topicId]: value
    }));
  };

  // Handle status button change
  const handleStatusChange = (topicId, newStatus) => {
    setTopicStatuses(prev => ({
      ...prev,
      [topicId]: newStatus
    }));
  };

  // Save Remarks and Status Changes
  const handleSave = async () => {
    try {
      if (!selectedTeacher || !selectedClass || !selectedSection || !selectedSubject) {
        setSnackbarMessage('Please select all filters');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
        return;
      }

      // Prepare topics to save
      const topicsToSave = topics.map(topic => {
        const currentStatus = topicStatuses[topic.topicId] || topic.status || 'P';
        const currentRemark = remarkInputs[topic.topicId] || '';
        const originalRemark = extractRemarkFromTopic(topic);
        const originalStatus = topic.status || 'P';

        // Check if there are any changes
        const hasChanges = currentStatus !== originalStatus || currentRemark !== originalRemark;

        if (hasChanges) {
          return {
            topicId: topic.topicId,
            status: currentStatus,
            coveredby_TeacherId: selectedTeacher.employeeId,
            coveredby_TeacherName: selectedTeacher.employeeName,
            description: currentRemark
          };
        }
        return null;
      }).filter(topic => topic !== null);

      if (topicsToSave.length === 0) {
        setSnackbarMessage('No changes to save');
        setOpenSnackbar(true);
        setSnackbarSeverity('warning');
        return;
      }

      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const branchId = isMaster ? selectedBranchId : sessionStorage.getItem('branchId');
      if (!branchId) {
        setSnackbarMessage('Branch ID is missing. Please select a branch first.');
        setOpenSnackbar(true);
        setSnackbarSeverity('warning');
        setLoadingBarProgress(0);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/TeachingTopics/MarkCovered?Branch_Id=${branchId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            topics: topicsToSave
          }),
        }
      );

      setLoadingBarProgress(100);

      if (response.ok) {
        setSnackbarMessage(`Saved successfully for ${topicsToSave.length} topic(s)`);
        setOpenSnackbar(true);
        setSnackbarSeverity('success');

        // Refresh topics data
        setTimeout(() => {
          fetchTopicsData(
            selectedClass.classId,
            selectedSection.sectionId,
            selectedSubject.subjectId
          );
        }, 500);
      } else {
        setSnackbarMessage('Failed to save changes');
        setOpenSnackbar(true);
        setSnackbarSeverity('error');
      }

    } catch (error) {
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity('error');
      setLoadingBarProgress(0);
    }
  };

  // Handle Cancel
  const handleCancel = () => {
    // Reset to original states
    const originalStatuses = {};
    const originalRemarks = {};

    topics.forEach(topic => {
      originalStatuses[topic.topicId] = topic.status || 'P';
      originalRemarks[topic.topicId] = extractRemarkFromTopic(topic);
    });

    setTopicStatuses(originalStatuses);
    setRemarkInputs(originalRemarks);

    setSnackbarMessage('Changes cancelled');
    setOpenSnackbar(true);
    setSnackbarSeverity('info');
  };

  // Handle dropdown changes
  const handleTeacherChange = (e) => {
    const selected = teachers.find((t) => t.employeeId === e.target.value);
    setSelectedTeacher(selected);
  };

  const handleClassChange = (e) => {
    const selected = classes.find((c) => c.classId === e.target.value);
    setSelectedClass(selected);
  };

  const handleSectionChange = (e) => {
    const selected = sections.find((s) => s.sectionId === e.target.value);
    setSelectedSection(selected);
  };

  const handleSubjectChange = (e) => {
    const selected = subjects.find((sub) => sub.subjectId === e.target.value);
    setSelectedSubject(selected);
  };

  // Display topics - use filteredTopics when search is active, otherwise use all topics
  const displayTopics = searchText.trim() ? filteredTopics : topics;

  // Calculate responsive widths based on screen size
  const getFilterWidths = () => {
    if (isExtraSmallScreen) {
      return {
        search: '100%',
        teacher: '100%',
        class: '100%',
        section: '100%',
        subject: '100%',
        gap: 1,
      };
    }
    if (isVerySmallScreen) {
      return {
        search: '48%',
        teacher: '48%',
        class: '48%',
        section: '48%',
        subject: '48%',
        gap: 1,
      };
    }
    if (isSmallScreen) {
      return {
        search: '19%',
        teacher: '19%',
        class: '19%',
        section: '19%',
        subject: '19%',
        gap: 1,
      };
    }
    return {
      search: '18%',
      teacher: '18%',
      class: '14%',
      section: '14%',
      subject: '18%',
      gap: 2,
    };
  };

  const filterWidths = getFilterWidths();

  // Info sections data for the Info Modal
  const infoSections = [
    {
      icon: <FilterListIcon sx={{ color: "#1976d2", fontSize: 22 }} />,
      title: "Filter Dropdowns",
      color: "#e3f2fd",
      borderColor: "#1976d2",
      items: [
        { label: "Teacher", desc: "Select a teacher first. All other dropdowns depend on this selection." },
        { label: "Class", desc: "Loaded after selecting a teacher. Shows classes assigned to that teacher." },
        { label: "Section", desc: "Loaded after selecting a class. Shows sections for that class and teacher." },
        { label: "Subject", desc: "Loaded after selecting a section. Topics are fetched automatically once a subject is chosen." },
        { label: "Branch", desc: "Master users can switch branches from the top-right dropdown. Teacher list reloads automatically." },
      ],
    },
    {
      icon: <TableChartIcon sx={{ color: "#7b1fa2", fontSize: 22 }} />,
      title: "Topics Table",
      color: "#f3e5f5",
      borderColor: "#7b1fa2",
      items: [
        { label: "Topic Name", desc: "The name of the teaching topic for the selected subject." },
        { label: "Status Chip", desc: "Shows current status — Pending (yellow) or Covered (green)." },
        { label: "Remark", desc: "A free-text field to enter notes about the topic's coverage. Pre-filled with any existing remarks from the server." },
        { label: "Search", desc: "Filter topics by name in real time. Disabled until a subject is selected. Press Enter or type to search instantly." },
      ],
    },
    {
      icon: <TrackChangesIcon sx={{ color: "#e65100", fontSize: 22 }} />,
      title: "Status Toggle",
      color: "#fff3e0",
      borderColor: "#e65100",
      items: [
        { label: "Pending", desc: "Marks the topic as not yet covered. Highlighted in red when selected." },
        { label: "Covered", desc: "Marks the topic as taught. Highlighted in green when selected. Records the teacher and date automatically on save." },
        { label: "Toggle Behaviour", desc: "Each row has its own independent toggle. You can update multiple topics at once before saving." },
      ],
    },
    {
      icon: <SaveIcon sx={{ color: "#2e7d32", fontSize: 22 }} />,
      title: "Actions",
      color: "#e8f5e9",
      borderColor: "#2e7d32",
      items: [
        { label: "Save", desc: "Only saves rows where status or remark has changed from the original. Topics with no changes are skipped automatically." },
        { label: "Cancel", desc: "Resets all status and remark fields back to their original server values without making any API calls." },
        { label: "No Changes", desc: "If Save is clicked but nothing was changed, a warning snackbar is shown instead of submitting." },
      ],
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: '#0B1F3D',
          borderRadius: '8px 8px 0 0',
          mb: 0
        }}
      >
        <Toolbar sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1.5,
          px: 3,
        }}>
          {/* Left: Title + Info Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h4" component="div" sx={{ fontWeight: 600, letterSpacing: '0.5px', color: '#ffffff' }}>
              Topic Progress
            </Typography>

            {/* Info Button */}
            <Tooltip title="View page information & guide" arrow placement="right">
              <IconButton
                onClick={() => setInfoModalOpen(true)}
                size="small"
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  backgroundColor: "rgba(255,255,255,0.1)",
                  border: "1.5px solid rgba(255,255,255,0.25)",
                  borderRadius: "50%",
                  width: 32,
                  height: 32,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#1976d2",
                    borderColor: "#64B5F6",
                    color: "#ffffff",
                    boxShadow: "0 0 0 3px rgba(100,181,246,0.25)",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Right: Branch Dropdown */}
          {isMaster && (
            <Box sx={{ position: 'relative' }}>
              <FormControl size="small" sx={{ width: 220 }}>
                <InputLabel
                  sx={{
                    color: 'rgba(255,255,255,0.6)',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    top: '-2px',
                    '&.Mui-focused': { color: '#64B5F6' },
                    '&.MuiFormLabel-filled': { color: 'rgba(255,255,255,0.7)' },
                  }}
                >
                  Branch
                </InputLabel>
                <Select
                  value={selectedBranchId || ''}
                  onChange={(e) => setSelectedBranchId(e.target.value)}
                  label="Branch"
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <em>Select Branch</em>;   // fallback when nothing selected
                    }
                    const branch = branches.find(b => b.branchId === selected);
                    return branch ? branch.branchName : selected;
                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', ml: 0.5 }} />
                    </InputAdornment>
                  }
                  sx={{
                    color: '#ffffff',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    letterSpacing: '0.3px',
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255,255,255,0.15)',
                      borderWidth: '1px',
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.11)',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.35)' },
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      boxShadow: '0 0 0 3px rgba(100,181,246,0.18)',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#64B5F6', borderWidth: '1.5px' },
                    },
                    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' },
                    '&.Mui-focused .MuiSelect-icon': { color: '#64B5F6' },
                    '& .MuiSelect-select': { py: 1.1 },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        mt: 0.5,
                        borderRadius: '10px',
                        border: '1px solid rgba(11,31,61,0.12)',
                        boxShadow: '0 8px 32px rgba(11,31,61,0.18), 0 2px 8px rgba(0,0,0,0.08)',
                        '& .MuiList-root': { py: 0.5 },
                      },
                    },
                  }}
                >
                  {branches.map((branch) => (
                    <MenuItem
                      key={branch.branchId}
                      value={branch.branchId}
                      sx={{
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        color: '#0B1F3D',
                        px: 2,
                        py: 1.1,
                        mx: 0.5,
                        borderRadius: '6px',
                        '&:hover': { backgroundColor: 'rgba(11,31,61,0.07)' },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(25,118,210,0.1)',
                          color: '#1565C0',
                          fontWeight: 600,
                          '&:hover': { backgroundColor: 'rgba(25,118,210,0.15)' },
                        },
                      }}
                    >
                      {branch.branchName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Paper
        elevation={3}
        sx={{
          padding: 3,
          width: '100%',
          margin: 'auto',
          borderRadius: '0 0 8px 8px',
          backgroundColor: '#fafafa'
        }}
      >
        <LoadingBar progress={loadingBarProgress} color="rgb(152, 106, 182)" height={5} />

        {/* Search and Filters */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
            gap: 2,
            flexWrap: { xs: 'wrap', sm: 'nowrap' }
          }}
        >
          <TextField
            placeholder="Search by topic name..."
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            fullWidth
            disabled={!selectedSubject}
            sx={{
              width: { xs: '100%', sm: '350px' },
              backgroundColor: '#ffffff',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#0B1F3D',
                },
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Dropdowns for Teacher, Class, Section, Subject */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 2.5,
            mb: 3,
            p: 2,
            backgroundColor: '#ffffff',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
          }}
        >
          <FormControl fullWidth size="small">
            <InputLabel htmlFor="teacher" shrink={true}>Teacher</InputLabel>
            <Select
              id="teacher"
              value={selectedTeacher?.employeeId || ''}
              onChange={handleTeacherChange}
              label="Teacher"
              sx={{ backgroundColor: '#fff' }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: '#757575' }}>Select Teacher</span>;
                }
                const teacher = teachers.find(t => t.employeeId === selected);
                return teacher?.employeeName || selected;
              }}
            >
              {teachers.map((teacher) => (
                <MenuItem key={teacher.employeeId} value={teacher.employeeId}>
                  {teacher.employeeName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel htmlFor="class" shrink={true}>Class</InputLabel>
            <Select
              id="class"
              value={selectedClass?.classId || ''}
              onChange={handleClassChange}
              label="Class"
              disabled={!selectedTeacher}
              sx={{ backgroundColor: '#fff' }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: '#757575' }}>Select Class</span>;
                }
                const cls = classes.find(c => c.classId === selected);
                return cls?.className || selected;
              }}
            >
              {classes.map((cls) => (
                <MenuItem key={cls.classId} value={cls.classId}>
                  {cls.className}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel htmlFor="section" shrink={true}>Section</InputLabel>
            <Select
              id="section"
              value={selectedSection?.sectionId || ''}
              onChange={handleSectionChange}
              label="Section"
              disabled={!selectedClass}
              sx={{ backgroundColor: '#fff' }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: '#757575' }}>Select Section</span>;
                }
                const section = sections.find(s => s.sectionId === selected);
                return section?.sectionName || selected;
              }}
            >
              {sections.map((section) => (
                <MenuItem key={section.sectionId} value={section.sectionId}>
                  {section.sectionName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel htmlFor="subject" shrink={true}>Subject</InputLabel>
            <Select
              id="subject"
              value={selectedSubject?.subjectId || ''}
              onChange={handleSubjectChange}
              label="Subject"
              disabled={!selectedSection}
              sx={{ backgroundColor: '#fff' }}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return <span style={{ color: '#757575' }}>Select Subject</span>;
                }
                const subject = subjects.find(s => s.subjectId === selected);
                return subject?.subjectName || selected;
              }}
            >
              {subjects.map((subject) => (
                <MenuItem key={subject.subjectId} value={subject.subjectId}>
                  {subject.subjectName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Topics Table */}
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}
        >
          <Table
            sx={{
              minWidth: 650,
              '& .MuiTableCell-root': {
                borderRight: '1px solid #e0e0e0',
                fontSize: '0.95rem',
                padding: '14px 16px',
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                fontWeight: 700,
                backgroundColor: '#f8f9fa',
                color: '#2c3e50',
                borderBottom: '2px solid #dee2e6',
                textTransform: 'uppercase',
                fontSize: '0.85rem',
                letterSpacing: '0.5px',
              },
              '& .MuiTableBody-root .MuiTableRow-root': {
                '&:hover': {
                  backgroundColor: '#f5f7fa',
                  transition: 'background-color 0.2s ease',
                },
                '&:last-child td': {
                  borderBottom: 'none',
                }
              }
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Topic Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Remark</TableCell>
                <TableCell sx={{ textAlign: 'center', borderRight: 'none !important' }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayTopics.length > 0 ? (
                displayTopics.map((topic) => {
                  const currentStatus = topicStatuses[topic.topicId] || topic.status || 'P';
                  return (
                    <TableRow
                      key={topic.topicId}
                      sx={{
                        backgroundColor: '#ffffff',
                      }}
                    >
                      <TableCell>{topic.topicName}</TableCell>
                      <TableCell>
                        <Chip
                          label={currentStatus === 'P' ? 'Pending' : 'Covered'}
                          color={currentStatus === 'C' ? 'success' : 'error'}
                          sx={{
                            fontWeight: 'bold',
                            backgroundColor: currentStatus === 'C' ? '#d4edda' : '#fff3cd',
                            color: currentStatus === 'C' ? '#155724' : '#856404',
                            fontSize: '0.75rem',
                            height: '24px'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={remarkInputs[topic.topicId] || ''}
                          onChange={(e) => handleRemarkChange(topic.topicId, e.target.value)}
                          placeholder="Add remark..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1,
                              backgroundColor: '#fff',
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <ToggleButtonGroup
                          value={currentStatus}
                          exclusive
                          onChange={(e, newStatus) => {
                            if (newStatus !== null) {
                              handleStatusChange(topic.topicId, newStatus);
                            }
                          }}
                          size="small"
                          sx={{
                            '& .MuiToggleButton-root': {
                              px: 2,
                              py: 0.5,
                              fontSize: '0.75rem',
                              backgroundColor: 'transparent',
                              color: 'inherit',
                              border: '1px solid #ccc',
                              '&.Mui-selected': {
                                color: 'white',
                              },
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              },
                            }
                          }}
                        >
                          <ToggleButton
                            value="P"
                            sx={{
                              '&.Mui-selected': {
                                backgroundColor: '#b71c1c',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#9a0007',
                                }
                              },
                              color: 'inherit',
                              backgroundColor: 'transparent',
                            }}
                          >
                            Pending
                          </ToggleButton>
                          <ToggleButton
                            value="C"
                            sx={{
                              '&.Mui-selected': {
                                backgroundColor: '#1b5e20',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: '#003300',
                                }
                              },
                              color: 'inherit',
                              backgroundColor: 'transparent',
                            }}
                          >
                            Covered
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6, borderRight: 'none !important' }}>
                    <Typography variant="body1" sx={{ color: '#999', fontWeight: 500 }}>
                      {searchText.trim() ? `No topics found matching "${searchText}"` : 'No topics available. Please select a subject first.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Save and Cancel Buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: 3,
            flexWrap: 'wrap'
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCancel}
            disabled={!selectedSubject}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={!selectedSubject}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              px: 3,
            }}
          >
            Save
          </Button>
        </Box>

        {/* Snackbar for notifications */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={4000}
          onClose={() => setOpenSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Paper>

      {/* Info Modal */}
      <Modal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95vw", sm: 600, md: 660 },
            maxHeight: "85vh",
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: "0 24px 64px rgba(11,31,61,0.22)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Info Modal Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #0B1F40 0%, #1565C0 100%)",
              padding: "16px 20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  backgroundColor: "rgba(255,255,255,0.15)",
                  borderRadius: "50%",
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <InfoOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography id="info-modal-title" variant="h6" sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}>
                  Topic Progress — Guide
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)" }}>
                  Complete reference for all features and fields
                </Typography>
              </Box>
            </Box>
            
          </Box>

          {/* Info Modal Scrollable Body */}
          <Box sx={{ overflowY: "auto", p: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>

            {/* Page Overview */}
            <Box sx={{ backgroundColor: "#f0f4ff", border: "1px solid #c5d3f0", borderRadius: 2, p: 2 }}>
              <Typography variant="body2" sx={{ color: "#1a237e", lineHeight: 1.7 }}>
                The <strong>Topic Progress</strong> page lets teachers track and update the coverage status of teaching topics for any class, section, and subject. Mark topics as Pending or Covered and add remarks before saving.
              </Typography>
            </Box>

            {/* Sections */}
            {infoSections.map((section, sIdx) => (
              <Box
                key={sIdx}
                sx={{
                  backgroundColor: section.color,
                  border: `1px solid ${section.borderColor}30`,
                  borderLeft: `4px solid ${section.borderColor}`,
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  {section.icon}
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1a1a2e", fontSize: "0.95rem" }}>
                    {section.title}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                  {section.items.map((item, iIdx) => (
                    <Box key={iIdx} sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <Chip
                        label={item.label}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          height: 22,
                          flexShrink: 0,
                          backgroundColor: `${section.borderColor}18`,
                          color: section.borderColor,
                          border: `1px solid ${section.borderColor}40`,
                          mt: "1px",
                        }}
                      />
                      <Typography variant="body2" sx={{ color: "#424242", lineHeight: 1.5, fontSize: "0.82rem" }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}

            {/* Tip Box */}
            <Box sx={{ backgroundColor: "#fffde7", border: "1px solid #f9a825", borderRadius: 2, p: 2, display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Typography sx={{ fontSize: "1.1rem", mt: "1px" }}>💡</Typography>
              <Typography variant="body2" sx={{ color: "#5d4037", lineHeight: 1.65, fontSize: "0.82rem" }}>
                <strong>Pro Tip:</strong> You can update the status and remarks of multiple topics at once before clicking Save — only rows with actual changes are sent to the server, keeping things efficient. Use Cancel to undo all unsaved edits at once.
              </Typography>
            </Box>

          </Box>

          {/* Info Modal Footer */}
          <Box
            sx={{
              borderTop: "1px solid #e0e0e0",
              px: 2.5,
              py: 1.5,
              display: "flex",
              justifyContent: "flex-end",
              flexShrink: 0,
              backgroundColor: "#fafafa",
            }}
          >
            <Button
              onClick={() => setInfoModalOpen(false)}
              variant="contained"
              color="primary"
              sx={{ fontWeight: 600, px: 3 }}
            >
              Got It
            </Button>
          </Box>
        </Box>
      </Modal>

    </Container>
  );

};

export default TopicProgress;